// Page search: what one person can find, and how typed words are matched to it.
//
// The list is built from what THIS person holds — their menu rows and the dashboards
// those rows allow — so search can never advertise a page the top bar would not show
// them. Names come from the menu and from the dashboards; the line under each name and
// the extra plain words ("vacant", "payout") come from the catalogue. A dashboard's
// lists come from its file in components/dashboard/lists, which is also where the slug
// that opens each one lives.
import { Info } from 'lucide-react';
import { LISTS as APPEALS_LIST_INFO } from '../components/dashboard/lists/appeals';
import { LISTS as APPROVALS_LIST_INFO } from '../components/dashboard/lists/approvals';
import { LISTS as ATTENDANCE_LIST_INFO } from '../components/dashboard/lists/attendance';
import { LISTS as DEPLOYMENT_LIST_INFO } from '../components/dashboard/lists/deployment';
import { LISTS as MOOD_LIST_INFO } from '../components/dashboard/lists/mood';
import { LISTS as ONBOARDING_LIST_INFO } from '../components/dashboard/lists/onboarding';
import { LISTS as RED_FLAG_LIST_INFO } from '../components/dashboard/lists/redFlag';
import { LISTS as SCORE_MOVEMENT_LIST_INFO } from '../components/dashboard/lists/scoreMovement';
import { LISTS as TRAINING_LIST_INFO } from '../components/dashboard/lists/training';
import type { DashboardListInfo } from '../components/dashboard/lists/types';
import { LISTS as UNIFORM_LIST_INFO } from '../components/dashboard/lists/uniform';
import { LISTS as YELLOW_FLAG_LIST_INFO } from '../components/dashboard/lists/yellowFlag';
import type { MenuItem } from '../stores/menuStore';
import { DASHBOARD_INFO, normalisePath, pageInfoFor, sectionByMenuCode } from './catalog';
import { DASHBOARD_HOME_PATH, allowedDashTabs, dashLabel, dashboardListPath, dashboardPath, type DashTab } from './dashboards';
import { menuIcon, type MenuIcon } from './menuIcon';
import { holdsDashboard, menuChildren, menuCodes, pageMenuItems, pagesUnder, type MenuRules } from './menuTree';
import { isSectionRow } from './trail';

export type SearchKind = 'dashboard' | 'page' | 'list' | 'term';

export interface SearchEntry {
  id: string;
  kind: SearchKind;
  name: string;
  /** One line under the name: what the page is for, or what the term means. */
  line: string;
  /** Where it lives, in a word or two: "Reports", "In Deployment", "What it means". */
  where: string;
  to: string;
  /** Plain words a person might type that are not in the name. */
  keywords: string;
  icon: MenuIcon;
}

/**
 * The lists each dashboard opens by address (?list=<slug>), in the order it shows them.
 * Attrition opens one list by address (?list=exits, its month's leavers, from the home
 * screen's card) but none here: it is a pop-up over the month, not something to search
 * for. Data Hygiene has none here either: its checks
 * open by ?list= too, but it has no lists file to name them in. Keyed by every
 * dashboard, so a new one cannot be added without saying what its lists are.
 */
export const DASHBOARD_LISTS: Record<DashTab, readonly DashboardListInfo[]> = {
  Deployment: DEPLOYMENT_LIST_INFO,
  Attendance: ATTENDANCE_LIST_INFO,
  Attrition: [],
  'Red Flag': RED_FLAG_LIST_INFO,
  'Yellow Flag': YELLOW_FLAG_LIST_INFO,
  Appeals: APPEALS_LIST_INFO,
  Mood: MOOD_LIST_INFO,
  Approvals: APPROVALS_LIST_INFO,
  Onboarding: ONBOARDING_LIST_INFO,
  Training: TRAINING_LIST_INFO,
  'Score Movement': SCORE_MOVEMENT_LIST_INFO,
  'Uniform & Asset Management': UNIFORM_LIST_INFO,
  'Data Hygiene': [],
  // Its search and status filter are kept in the page, never in the address.
  Hiring: [],
  // One table: its search and sort are kept in the page.
  EWS: [],
};

// Among equally good matches: whole dashboards and pages first, then the lists inside a
// dashboard, then the meanings of words.
const KIND_ORDER: Record<SearchKind, number> = { dashboard: 0, page: 1, list: 2, term: 3 };

/** Everything this person can open, in menu order: dashboards (each with its lists and terms), then every section and page. */
export function buildSearchIndex(items: MenuItem[], rules: MenuRules): SearchEntry[] {
  const pages = pageMenuItems(items, rules);
  const out: SearchEntry[] = [];
  const add = (entry: Omit<SearchEntry, 'id'>) => {
    out.push({ ...entry, id: `${entry.kind}:${entry.to}:${entry.name}` });
  };

  if (holdsDashboard(items)) {
    const dashboardName = pages.find((m) => m.path === DASHBOARD_HOME_PATH)?.name || 'Dashboard';
    for (const tab of allowedDashTabs(menuCodes(items))) {
      const info = DASHBOARD_INFO[tab];
      const name = dashLabel(tab);
      const to = dashboardPath(tab);
      add({ kind: 'dashboard', name, line: info.question, where: dashboardName, to, keywords: info.keywords, icon: info.icon });
      // Each list opens ON that list. Only the dashboards this person holds are walked,
      // so a list is never offered on a dashboard they cannot open.
      for (const list of DASHBOARD_LISTS[tab]) {
        add({ kind: 'list', name: list.name, line: list.line, where: `In ${name}`, to: dashboardListPath(tab, list.slug), keywords: list.keywords ?? '', icon: info.icon });
      }
      for (const [term, meaning] of info.glossary) {
        // ?help=1 opens the dashboard with its "How to read this" panel showing.
        add({ kind: 'term', name: term, line: meaning, where: 'What it means', to: `${to}?help=1`, keywords: '', icon: Info });
      }
    }
  }

  // One row per address: a menu can carry the same page twice (an old address beside
  // its new one), and the first in menu order is the one the top bar shows first.
  const seen = new Set<string>();
  const addPage = (m: MenuItem, where: string) => {
    if (!m.path) return;
    const address = normalisePath(m.path);
    if (seen.has(address)) return;
    seen.add(address);
    const info = pageInfoFor(m.path);
    add({ kind: 'page', name: m.name, line: info?.line ?? '', where, to: m.path, keywords: info?.keywords ?? '', icon: menuIcon(m.icon) });
  };

  for (const top of menuChildren(pages, null)) {
    const under = pagesUnder(pages, top);
    const section = sectionByMenuCode(top.code);
    if (section?.indexPath && under.length > 0) {
      add({ kind: 'page', name: top.name, line: section.blurb, where: 'Top menu', to: section.indexPath, keywords: '', icon: menuIcon(top.icon) });
    }
    // A top-level row with pages under it is a heading; its own address is its first page's.
    if (!isSectionRow(pages, top)) addPage(top, 'Top menu');
    for (const m of under) addPage(m, top.name);
  }

  return out;
}

const escapeForPattern = (word: string) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// How much of what was typed is turned into patterns. A browser refuses a pattern built
// from one very long unbroken piece of text (a pasted token, an encoded image) and the
// error would take the whole screen down. No page is found by more than this anyway.
const MAX_WORDS = 12;
const MAX_WORD_LENGTH = 64;

/**
 * The entries that match what was typed, best first.
 *
 * Every typed word must match at the START of a word, so "late" finds "late sign-in"
 * but not "template". Matches in the name come first, then matches that needed the
 * extra plain words, then matches that needed the line under the name.
 */
export function searchPages(index: SearchEntry[], query: string): SearchEntry[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean)
    .slice(0, MAX_WORDS)
    .map((w) => w.slice(0, MAX_WORD_LENGTH));
  if (words.length === 0) return [];
  const tests = words.map((w) => new RegExp(`(^|[^a-z0-9])${escapeForPattern(w)}`));
  const allIn = (text: string) => tests.every((t) => t.test(text));

  const found: Array<{ entry: SearchEntry; score: number; order: number }> = [];
  index.forEach((entry, order) => {
    const name = entry.name.toLowerCase();
    const withKeywords = `${name} ${entry.keywords.toLowerCase()}`;
    const withLine = `${withKeywords} ${entry.line.toLowerCase()}`;
    const tier = allIn(name) ? 0 : allIn(withKeywords) ? 1 : allIn(withLine) ? 2 : -1;
    if (tier < 0) return;
    found.push({ entry, score: tier * 10 + KIND_ORDER[entry.kind], order });
  });
  return found.sort((a, b) => a.score - b.score || a.order - b.order).map((f) => f.entry);
}

/** The dashboards and pages at these addresses, in the order given. Addresses the person no longer holds are dropped. */
export function entriesForPaths(index: SearchEntry[], paths: string[]): SearchEntry[] {
  const out: SearchEntry[] = [];
  for (const path of paths) {
    const wanted = normalisePath(path);
    const hit = index.find((e) => (e.kind === 'dashboard' || e.kind === 'page') && normalisePath(e.to) === wanted);
    if (hit && !out.includes(hit)) out.push(hit);
  }
  return out;
}
